import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MenuIcon } from '@/components/MenuIcon';
import { apiFetch } from '@/lib/api/client';
import { useCanManage } from '@/lib/auth/permissions';
import { queryKeys } from '@/lib/queryKeys';
import { relativeDay } from '@/pages/home/widgets/format';
import { WidgetFrame } from '@/pages/home/widgets/WidgetFrame';

/**
 * 미처리 개선사항 요청 — 사이드바의 &lsquo;개선사항 요청&rsquo; 으로 들어온 건.
 *
 * ## 창구를 하나로 합쳤다 (사용자 지시 2026-09-01)
 *
 * 한때 이 위젯은 요청게시판(`board_request_posts`)을 보다가 접수 창구(`sys_improvement_requests`)로
 * 옮겨 갔다. 그런데 <b>둘 다 「개선요청」이라 불렸고</b>, 헤더 버튼으로 낸 것과 사이드바 화면에
 * 보이는 것이 서로 다른 표였다 — 낸 사람은 목록에서 자기 글을 못 찾았다.
 *
 * 이제 <b>요청게시판 하나만 본다.</b> 헤더 모달도 AI 대화도 같은 곳으로 들어간다.
 *
 * ## 받는 쪽의 큐다
 * 이 서비스의 개선사항을 **받아 처리하는 사람들**(전산팀·공사전략팀)의 화면이다. 그래서 카탈로그에
 * `improvement_handle` 권한을 걸어, 그 밖의 계정에는 위젯 목록에도 나오지 않는다.
 *
 * 카탈로그에서 감추는 것은 UX 일 뿐이다. **목록 자체는 전 직원이 본다** — 무엇을 요청했고 어떻게
 * 답을 받았는지는 가릴 이유가 없다. 대신 **요청자 이름만 서버가 덜어낸다**(`RequesterVisibility`,
 * 2026-09-01): 전산 담당이 아니면 이 뱃지에 「익명」이 들어온다. 화면이 거르는 구조였다면 요청
 * 한 줄로 이름이 샜을 것이다.
 */

const VISIBLE_COUNT = 4;

/** 요청게시판 한 줄(`RequestPostResponse`). 완료 여부는 `completed` 한 칸이다. */
type RequestPost = {
  id: string;
  /** 전산 담당이 아니면 서버가 「익명」으로 바꿔 내려준다(`RequesterVisibility`) */
  requesterName: string;
  requestContent: string;
  responseContent: string | null;
  completed: boolean;
  feasibility: 'PENDING' | 'ACCEPTED' | 'REVIEWING' | 'REJECTED' | null;
  estimatedPeriod: string | null;
  createdAt: string;
  updatedAt: string;
};

export function ImprovementRequestsWidget() {
  // 처리 버튼은 `improvement_handle` 수정 권한으로 낸다. 관리자는 이 판정에 자동으로 포함된다
  // (`resolveCanManage`). 버튼 노출은 UX 이고, 실제 처리는 서버가 같은 키로 다시 막는다.
  const canHandle = useCanManage('improvement_handle');
  const queryClient = useQueryClient();

  const requests = useQuery({
    queryKey: queryKeys.requestBoardPosts,
    queryFn: () => apiFetch<RequestPost[]>('/v1/request-posts'),
  });

  const handle = useMutation({
    // 요청게시판의 완료 처리는 `/answer` 하나다 — 답변과 완료가 같은 자리에서 일어난다.
    // 여기서는 위젯의 「처리」 버튼이라 답변 없이 완료만 세운다.
    mutationFn: (id: string) =>
      apiFetch<RequestPost>(`/v1/request-posts/${id}/answer`, {
        method: 'PUT',
        body: { responseContent: null, completed: true },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.requestBoardPosts }),
  });

  // 미처리만 본다. 서버에 `openOnly` 가 없어 여기서 거른다 — 요청게시판은 전 직원이 보는
  // 목록이라 범위를 좁히는 판정이 아니고, **몇 건이 남았나** 를 그리는 위젯이다.
  const open = (requests.data ?? []).filter((row) => !row.completed);

  return (
    <WidgetFrame
      title="미처리 개선사항 요청"
      icon="message"
      pending={requests.isPending}
      error={requests.error}
      empty={open.length === 0}
      emptyText={canHandle ? '처리할 개선요청이 없습니다.' : '접수한 개선요청이 없습니다.'}
      headerRight={open.length > 0 ? <span className="badge badge-yellow">{open.length}건</span> : undefined}
    >
      <div className="space-y-0.5">
        {open.slice(0, VISIBLE_COUNT).map((request) => (
          <div key={request.id} className="group flex items-center gap-2 py-1.5 px-1 rounded-md hover:bg-slate-50">
            {/*
              여기서는 <b>내려온 이름을 쓰지 않는다</b>(사용자 지시 2026-09-02). 서버는 전산 담당·
              관리자에게 실명을 그대로 준다(`RequesterVisibility`) — 그 자격으로 보는 사람의 홈
              화면에 이름이 뜨면, 목록에 「익명」이라 적어 둔 약속이 화면마다 달라진다. 이미 쌓인
              글도 여기서는 똑같이 「익명」으로 보인다.
            */}
            <span className="badge badge-gray flex-shrink-0 badge-sm" style={{ maxWidth: '80px' }}>
              <span className="truncate">익명</span>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-ui-md" style={{ color: 'var(--text-secondary)' }}>
                {firstLine(request.requestContent)}
              </span>
              <span className="block truncate text-ui-xs" style={{ color: 'var(--text-muted)' }}>
                {relativeDay(request.createdAt)}
              </span>
            </span>
            {/* 반영여부는 담당자가 답한 값이다(V170). 「접수」는 기본값이라 뱃지를 달지 않는다 —
                모든 줄에 같은 뱃지가 붙으면 그 칸이 아무것도 말하지 않는다. */}
            {request.feasibility === 'REVIEWING' && <span className="badge badge-amber flex-shrink-0">검토중</span>}
            {request.feasibility === 'ACCEPTED' && <span className="badge badge-blue flex-shrink-0">반영예정</span>}
            {request.feasibility === 'REJECTED' && <span className="badge badge-red flex-shrink-0">반영불가</span>}
            {canHandle && (
              <button
                onClick={() => handle.mutate(request.id)}
                disabled={handle.isPending}
                className="btn-ghost p-0.5 opacity-0 group-hover:opacity-100 flex-shrink-0"
                aria-label={`${firstLine(request.requestContent)} 처리 완료`}
                title="처리 완료로 표시"
              >
                <MenuIcon name="check" size={13} />
              </button>
            )}
          </div>
        ))}

        {open.length > VISIBLE_COUNT && (
          <p className="pt-1 text-center text-ui-xs" style={{ color: 'var(--text-muted)' }}>
            외 {open.length - VISIBLE_COUNT}건
          </p>
        )}
      </div>
    </WidgetFrame>
  );
}

/** 요청 본문은 여러 줄이다. 좁은 칸에는 첫 줄만 넣는다. */
function firstLine(content: string): string {
  return content.split(/\r?\n/)[0] ?? content;
}
